const RECOVERY_STATE_KEY = "jurivia_asset_recovery";
const RECOVERY_QUERY_PARAM = "__jurivia_recovery";
const RECOVERY_WINDOW_MS = 2 * 60_000;
const MAX_RECOVERY_ATTEMPTS = 2;

// Note: `reading 'component'` / `property "component"` correspond au crash de
// TanStack Router lorsqu'un « match » de route hérité d'un build précédent ne
// trouve plus sa route dans l'arbre du nouveau build (décalage de déploiement).
// Variante Safari : `undefined is not an object (evaluating 'e[t??default]')`
// — même cause (chunk périmé dont l'export `default` a disparu), formulation
// propre à JavaScriptCore. Sans elle, Safari affichait un écran blanc au lieu
// de déclencher la récupération (vidage de cache + rechargement).
const CHUNK_LOAD_ERROR_RE =
  /loading chunk|failed to fetch dynamically imported module|dynamically imported module|importing a module script|module script|cannot read properties of undefined \(reading '(?:default|__esModule|_result|component)'\)|can't access property "(?:default|__esModule|_result|component)"|undefined is not an object \(evaluating '[^']*(?:default|__esModule|_result|component)[^']*'\)|_result is undefined|unexpected token '<'|expected expression, got '<'/i;

type RecoveryState = {
  attempts: number;
  lastAttemptAt: number;
};

let recoveryInstalled = false;
let recoveryInFlight = false;

function readRecoveryState(): RecoveryState {
  if (typeof window === "undefined") {
    return { attempts: 0, lastAttemptAt: 0 };
  }

  try {
    const raw = window.sessionStorage.getItem(RECOVERY_STATE_KEY);
    if (!raw) return { attempts: 0, lastAttemptAt: 0 };

    const parsed = JSON.parse(raw) as Partial<RecoveryState>;
    return {
      attempts: Number(parsed.attempts) || 0,
      lastAttemptAt: Number(parsed.lastAttemptAt) || 0,
    };
  } catch {
    return { attempts: 0, lastAttemptAt: 0 };
  }
}

function writeRecoveryState(state: RecoveryState) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(RECOVERY_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures — recovery still proceeds.
  }
}

function clearRecoveryState() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(RECOVERY_STATE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const message = "message" in error ? String(error.message ?? "") : "";
    const reason = "reason" in error ? String(error.reason ?? "") : "";
    return [message, reason].filter(Boolean).join(" ").trim();
  }

  return String(error ?? "");
}

function buildRecoveryUrl(currentUrl: string, nonce: number) {
  const nextUrl = new URL(currentUrl);
  nextUrl.searchParams.set(RECOVERY_QUERY_PARAM, String(nonce));
  return nextUrl.toString();
}

function isAppRuntimeCache(cacheName: string) {
  return (
    /^js-chunks-v\d+$/.test(cacheName) ||
    cacheName === "google-fonts" ||
    /(^|-)precache-v\d+-|(^|-)runtime-/.test(cacheName)
  );
}

function isFormerAppShellWorker(registration: ServiceWorkerRegistration) {
  const scriptUrls = [
    registration.active?.scriptURL,
    registration.waiting?.scriptURL,
    registration.installing?.scriptURL,
  ].filter(Boolean);

  return scriptUrls.some((scriptUrl) => scriptUrl ? new URL(scriptUrl).pathname === "/sw.js" : false);
}

async function clearRuntimeCaches() {
  if (typeof window === "undefined") return;

  const tasks: Promise<unknown>[] = [];

  if ("caches" in window) {
    tasks.push(
      window.caches
        .keys()
        .then((keys) => Promise.allSettled(keys.filter(isAppRuntimeCache).map((key) => window.caches.delete(key))))
    );
  }

  if ("serviceWorker" in navigator) {
    tasks.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.allSettled(
            registrations
              .filter(isFormerAppShellWorker)
              .map((registration) => registration.unregister())
          )
        )
    );
  }

  await Promise.allSettled(tasks);
}

export function isChunkLoadErrorLike(error: unknown): boolean {
  return CHUNK_LOAD_ERROR_RE.test(normalizeErrorMessage(error));
}

export async function recoverFromChunkError(
  source: string,
  error?: unknown,
): Promise<boolean> {
  if (typeof window === "undefined" || recoveryInFlight) {
    return false;
  }

  // Skip recovery reload for bots/crawlers — they don't benefit from cache
  // clearing and a forced reload risks loops in their crawl budget.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/bot|crawler|spider|bingpreview|slurp|facebookexternalhit|embedly|quora link preview|whatsapp|telegrambot|headlesschrome/i.test(ua)) {
    console.warn("[chunk-recovery] Skipping reload for bot UA", { source, ua });
    return false;
  }

  const now = Date.now();
  const state = readRecoveryState();
  const isWindowExpired = now - state.lastAttemptAt > RECOVERY_WINDOW_MS;
  const attempts = isWindowExpired ? 0 : state.attempts;

  if (attempts >= MAX_RECOVERY_ATTEMPTS) {
    console.error("[chunk-recovery] Recovery budget exhausted", {
      source,
      error: normalizeErrorMessage(error),
    });
    return false;
  }

  recoveryInFlight = true;
  writeRecoveryState({ attempts: attempts + 1, lastAttemptAt: now });

  console.warn("[chunk-recovery] Recovering from asset load failure", {
    source,
    error: normalizeErrorMessage(error),
    attempt: attempts + 1,
  });

  await clearRuntimeCaches();
  window.location.replace(buildRecoveryUrl(window.location.href, now));
  return true;
}

export function installChunkLoadRecovery() {
  if (typeof window === "undefined" || recoveryInstalled) return;
  recoveryInstalled = true;

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    const details = event instanceof CustomEvent ? event.detail : undefined;
    void recoverFromChunkError("vite:preloadError", details);
  });

  window.addEventListener(
    "unhandledrejection",
    (event) => {
      if (!isChunkLoadErrorLike(event.reason)) return;

      event.preventDefault();
      void recoverFromChunkError("unhandledrejection", event.reason);
    },
    { capture: true },
  );

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLScriptElement || target instanceof HTMLLinkElement)) {
        return;
      }

      const assetUrl = target instanceof HTMLScriptElement ? target.src : target.href;
      if (!assetUrl || !/\/assets\/.+\.(js|css)(\?|$)/i.test(assetUrl)) {
        return;
      }

      void recoverFromChunkError("asset-load", assetUrl);
    },
    true,
  );
}

export function markAppBootSuccessful() {
  if (typeof window === "undefined") return;

  recoveryInFlight = false;
  clearRecoveryState();

  const url = new URL(window.location.href);
  if (!url.searchParams.has(RECOVERY_QUERY_PARAM)) return;

  url.searchParams.delete(RECOVERY_QUERY_PARAM);
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}